/**
 * CameraController Component
 * Animated camera mit GSAP
 * - Vertikale Animation zwischen Skulpturen
 * - Hero → Detail → Closeup Transition
 * - Uses bounding box for accurate camera positioning
 * - Calculates optimal distance for fit-to-screen
 */

import { useEffect, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import { PerspectiveCamera } from '@react-three/drei'
import gsap from 'gsap'
import { Vector3, PerspectiveCamera as ThreePerspectiveCamera } from 'three'
import { ViewMode } from '@/types/three'
import { BBoxInfo } from './SculptureModel'

// Camera FOV (must match PerspectiveCamera fov)
const CAMERA_FOV = 50
const FOV_RAD = (CAMERA_FOV * Math.PI) / 180

interface CameraControllerProps {
  viewMode: ViewMode
  targetY?: number
  bbox?: BBoxInfo
  transitionDuration?: number
  easing?: string
  onTransitionStart?: () => void
  onTransitionComplete?: () => void
}

/**
 * Calculate camera position based on view mode and bounding box
 */
function calculateCameraParams(
  viewMode: ViewMode,
  bbox: BBoxInfo | undefined,
  aspect: number,
  targetY: number
): { x: number; y: number; z: number; lookAtY: number } {
  let xOffset = 0
  let yOffset = 0
  let zPos = 4 // fallback
  let lookAtYOffset = 0

  if (viewMode === 'hero') {
    if (bbox) {
      const bboxHeight = bbox.max.y - bbox.min.y
      const bboxWidth = bbox.max.x - bbox.min.x

      // Calculate distance needed to fit height
      const margin = 1.3
      const distanceForHeight = (bboxHeight * margin / 2) / Math.tan(FOV_RAD / 2)

      // Calculate distance needed to fit width
      const horizontalFov = 2 * Math.atan(Math.tan(FOV_RAD / 2) * aspect)
      const distanceForWidth = (bboxWidth * margin / 2) / Math.tan(horizontalFov / 2)

      zPos = Math.max(distanceForHeight, distanceForWidth, 2.5)
      lookAtYOffset = bbox.center.y
      yOffset = bbox.center.y
    }
  } else if (viewMode === 'detail') {
    if (bbox) {
      const bboxHeight = bbox.max.y - bbox.min.y
      const bboxWidth = bbox.max.x - bbox.min.x
      const aspectRatio = bboxHeight / Math.max(bboxWidth, 0.001)

      // Tall works — "Die Hüter der Schwelle" (~2m stacked figures) or
      // "Geerdeter" (2m column) — don't have a head to frame: the whole
      // silhouette IS the subject. Zooming to the upper third with a
      // fixed z=1.0 pulls the camera so close that the tops get sliced
      // off the top of the viewport. Threshold ~2.5 catches those
      // works while leaving figurative pieces (aspect 1.5–2.2) on the
      // head-framed path with a subtle side-offset.
      if (aspectRatio > 2.5) {
        const margin = 1.08
        const distanceForHeight = (bboxHeight * margin / 2) / Math.tan(FOV_RAD / 2)
        const horizontalFov = 2 * Math.atan(Math.tan(FOV_RAD / 2) * aspect)
        const distanceForWidth = (bboxWidth * margin / 2) / Math.tan(horizontalFov / 2)
        zPos = Math.max(distanceForHeight, distanceForWidth, 2.0)
        xOffset = zPos * 0.2  // Gentle side-offset so it still differs from hero.
        lookAtYOffset = bbox.center.y
        yOffset = bbox.center.y
      } else {
        xOffset = 0.8
        zPos = 1.0
        lookAtYOffset = bbox.center.y + bboxHeight * 0.35
        yOffset = lookAtYOffset + 0.2
      }
    } else {
      xOffset = 0.8
      zPos = 1.0
      yOffset = 1.2
      lookAtYOffset = 1.0
    }
  } else if (viewMode === 'closeup') {
    xOffset = 1.5
    zPos = 1.5

    if (bbox) {
      lookAtYOffset = bbox.center.y
      yOffset = bbox.center.y + 0.2
    } else {
      yOffset = 0.3
      lookAtYOffset = 0
    }
  }

  return {
    x: xOffset,
    y: targetY + yOffset,
    z: zPos,
    lookAtY: targetY + lookAtYOffset,
  }
}

export function CameraController({
  viewMode,
  targetY = 0,
  bbox,
  transitionDuration = 2.0,
  easing = 'power2.inOut',
  onTransitionStart,
  onTransitionComplete,
}: CameraControllerProps) {
  const cameraRef = useRef<ThreePerspectiveCamera>(null)
  const lookAtRef = useRef(new Vector3(0, 0, 0))
  const initializedRef = useRef(false)
  const bboxRef = useRef<BBoxInfo | undefined>(undefined)

  const { viewport } = useThree()
  const aspect = viewport.width / viewport.height

  // Always keep bbox ref current
  bboxRef.current = bbox

  // Track previous values
  const prevViewModeRef = useRef<ViewMode>(viewMode)
  const prevTargetYRef = useRef<number>(targetY)
  const prevBBoxRef = useRef<BBoxInfo | undefined>(undefined)
  const isAnimatingRef = useRef(false)

  // Single effect for all camera logic
  useEffect(() => {
    if (!cameraRef.current) return

    const hasBBox = !!bboxRef.current
    const isTargetYChange = prevTargetYRef.current !== targetY
    const isViewModeChange = prevViewModeRef.current !== viewMode
    const isBBoxChange = prevBBoxRef.current !== bboxRef.current && hasBBox

    // Calculate params (works with or without bbox - uses fallback values)
    const params = calculateCameraParams(viewMode, bboxRef.current, aspect, targetY)

    // First init - snap or animate from far position
    const isFirstInit = !initializedRef.current
    if (isFirstInit) {
      // Wait for bbox on very first load for correct positioning
      if (!hasBBox) return

      initializedRef.current = true
      lookAtRef.current.set(0, params.lookAtY, 0)

      // Update all refs after first init
      prevViewModeRef.current = viewMode
      prevTargetYRef.current = targetY
      prevBBoxRef.current = bboxRef.current
    }

    // Determine if we need to animate
    const needsAnimation = isFirstInit || isViewModeChange || isTargetYChange

    // If bbox changed but nothing else, only update if not currently animating
    if (!needsAnimation && isBBoxChange && !isAnimatingRef.current) {
      // Smooth Z adjustment when bbox arrives after animation completed
      prevBBoxRef.current = bboxRef.current
      gsap.to(cameraRef.current.position, {
        z: params.z,
        duration: 0.5,
        ease: 'power2.out',
      })
      return
    }

    // No meaningful change — but aspect may have shifted (window resize).
    // Snap the camera distance to the new aspect without re-tweening,
    // so dragging the browser size doesn't queue dozens of GSAP timelines
    // that each allocate fresh tween state and stack the CPU/GPU load
    // (root cause of the resize-induced renderer OOM).
    if (!needsAnimation) {
      if (!isAnimatingRef.current && cameraRef.current.position.z !== params.z) {
        cameraRef.current.position.z = params.z
      }
      return
    }

    // Update refs
    prevViewModeRef.current = viewMode
    prevTargetYRef.current = targetY
    prevBBoxRef.current = bboxRef.current

    // Kill any running animations before starting new one
    gsap.killTweensOf(cameraRef.current.position)
    gsap.killTweensOf(lookAtRef.current)

    isAnimatingRef.current = true
    onTransitionStart?.()

    gsap.to(cameraRef.current.position, {
      x: params.x,
      y: params.y,
      z: params.z,
      duration: transitionDuration,
      ease: easing,
    })

    gsap.to(lookAtRef.current, {
      x: 0,
      y: params.lookAtY,
      z: 0,
      duration: transitionDuration,
      ease: easing,
      onUpdate: () => {
        if (cameraRef.current) {
          cameraRef.current.lookAt(lookAtRef.current)
        }
      },
      onComplete: () => {
        isAnimatingRef.current = false
        onTransitionComplete?.()
      },
    })
  }, [viewMode, targetY, bbox, transitionDuration, easing, onTransitionStart, onTransitionComplete, aspect])

  return (
    <PerspectiveCamera
      ref={cameraRef}
      makeDefault
      position={[0, 0, 12]}
      fov={50}
      near={0.1}
      far={1000}
    />
  )
}
